import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, LayoutGrid, List, Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { GroupIcon } from "@/lib/groupIcons";

export default function GroupsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = ["owner", "admin", "reviewer"].includes(user?.role);
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [newName, setNewName] = useState("");
  const [rename, setRename] = useState(null); // { id, name }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("coi_groups_view") || "grid");
  const changeView = (v) => { setView(v); localStorage.setItem("coi_groups_view", v); };
  const [density, setDensity] = useState(() => localStorage.getItem("coi_groups_density") || "comfortable");
  const changeDensity = (d) => { setDensity(d); localStorage.setItem("coi_groups_density", d); };

  const load = () => api.get("/groups").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  async function create(event) {
    event.preventDefault();
    if (!newName.trim()) return;
    try { await api.post("/groups", { name: newName.trim() }); setNewName(""); toast.success("Group created."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }
  async function saveRename() {
    try { await api.patch(`/groups/${rename.id}`, { name: rename.name.trim() }); setRename(null); toast.success("Group renamed."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }
  async function confirmDelete() {
    const group = pendingDelete; setPendingDelete(null);
    try { await api.delete(`/groups/${group.id}`); toast.success("Group deleted. No insureds were removed."); load(); }
    catch (error) { toast.error(formatApiError(error)); }
  }

  const visible = groups.filter((g) => g.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <section data-testid="groups-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Organize insureds</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">Groups</h1>
          <p className="mt-1 text-sm text-slate-600">Organize insureds into groups by team, location, project, or any category that fits how you work. An insured can belong to multiple groups.</p>
        </div>
      </div>

      {canWrite && (
        <form className="mt-6 flex max-w-md gap-2" data-testid="create-group-form" onSubmit={create}>
          <Input data-testid="new-group-input" onChange={(e) => setNewName(e.target.value)} placeholder="New group name" value={newName} />
          <Button data-testid="create-group-button" type="submit"><Plus size={16} />Add group</Button>
        </form>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input className="pl-9" data-testid="group-search-input" onChange={(e) => setQuery(e.target.value)} placeholder="Search groups" value={query} />
        </div>
        <div className="flex overflow-hidden rounded-md border" data-testid="groups-view-toggle">
          <button className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition ${view === "grid" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="groups-view-grid" onClick={() => changeView("grid")} type="button"><LayoutGrid size={14} />Grid</button>
          <button className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition ${view === "list" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="groups-view-list" onClick={() => changeView("list")} type="button"><List size={14} />List</button>
        </div>
        {view === "grid" ? <div className="flex overflow-hidden rounded-md border" data-testid="groups-density-toggle"><button className={`px-3 py-1.5 text-xs font-semibold transition ${density === "comfortable" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="groups-density-comfortable" onClick={() => changeDensity("comfortable")} type="button">Comfortable</button><button className={`px-3 py-1.5 text-xs font-semibold transition ${density === "compact" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="groups-density-compact" onClick={() => changeDensity("compact")} type="button">Compact</button></div> : null}
      </div>

      {view === "grid" ? (
      <div className={`mt-6 grid ${density === "compact" ? "gap-3 md:grid-cols-3 xl:grid-cols-4" : "gap-4 md:grid-cols-2 xl:grid-cols-3"}`}>
        {visible.map((group) => (
          <Card className="cursor-pointer border-slate-200 shadow-none transition hover:border-blue-300 hover:shadow-sm" data-testid={`group-card-${group.id}`} key={group.id} onClick={() => navigate(`/groups/${group.id}`)}>
            <CardContent className={`flex items-center justify-between gap-3 ${density === "compact" ? "p-3" : "p-5"}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-700"><GroupIcon name={group.icon} size={19} /></div>
                <div>
                  <h2 className="font-semibold text-slate-900">{group.name}</h2>
                  <p className="flex items-center gap-1 text-xs text-slate-500" data-testid={`group-member-count-${group.id}`}><Users size={12} />{group.memberCount} insured{group.memberCount === 1 ? "" : "s"}</p>
                </div>
              </div>
              {canWrite && (
                <div className="flex gap-1">
                  <Button data-testid={`group-rename-${group.id}`} onClick={(e) => { e.stopPropagation(); setRename({ id: group.id, name: group.name }); }} size="icon" variant="ghost"><Pencil size={15} /></Button>
                  <Button className="text-red-600 hover:text-red-700" data-testid={`group-delete-${group.id}`} onClick={(e) => { e.stopPropagation(); setPendingDelete(group); }} size="icon" variant="ghost"><Trash2 size={15} /></Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      ) : (
      <div className="mt-6 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {visible.map((group) => (
          <div className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3 transition hover:bg-slate-50" data-testid={`group-row-${group.id}`} key={group.id} onClick={() => navigate(`/groups/${group.id}`)}>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-blue-700"><GroupIcon name={group.icon} size={17} /></div>
              <div><h2 className="font-semibold text-slate-900">{group.name}</h2><p className="flex items-center gap-1 text-xs text-slate-500"><Users size={12} />{group.memberCount} insured{group.memberCount === 1 ? "" : "s"}</p></div>
            </div>
            {canWrite && (
              <div className="flex gap-1">
                <Button data-testid={`group-rename-${group.id}`} onClick={(e) => { e.stopPropagation(); setRename({ id: group.id, name: group.name }); }} size="icon" variant="ghost"><Pencil size={15} /></Button>
                <Button className="text-red-600 hover:text-red-700" data-testid={`group-delete-${group.id}`} onClick={(e) => { e.stopPropagation(); setPendingDelete(group); }} size="icon" variant="ghost"><Trash2 size={15} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {!visible.length && <div className="mt-12 grid min-h-56 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center"><div><Layers className="mx-auto text-slate-300" size={40} /><h2 className="mt-3 font-semibold">{query ? "No groups match your search" : "No groups yet"}</h2></div></div>}

      <Dialog onOpenChange={(open) => !open && setRename(null)} open={!!rename}>
        <DialogContent data-testid="group-rename-dialog">
          <DialogHeader><DialogTitle>Rename group</DialogTitle></DialogHeader>
          <Input data-testid="group-rename-input" onChange={(e) => setRename({ ...rename, name: e.target.value })} value={rename?.name || ""} />
          <DialogFooter><Button onClick={() => setRename(null)} variant="outline">Cancel</Button><Button data-testid="group-rename-save" onClick={saveRename}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog onOpenChange={(open) => !open && setPendingDelete(null)} open={!!pendingDelete}>
        <DialogContent data-testid="group-delete-dialog">
          <DialogHeader><DialogTitle>Delete “{pendingDelete?.name}”?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-600">Deleting this group will <span className="font-semibold text-slate-900">not delete or archive any insureds</span>. The {pendingDelete?.memberCount} insured{pendingDelete?.memberCount === 1 ? "" : "s"} in it will simply no longer be members of this group. This can’t be undone.</DialogDescription>
          <DialogFooter><Button onClick={() => setPendingDelete(null)} variant="outline" data-testid="group-delete-cancel">Cancel</Button><Button className="bg-red-600 hover:bg-red-700" data-testid="group-delete-confirm" onClick={confirmDelete}>Delete group</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
